import { Component, Input, OnInit } from '@angular/core';
import {
  CustomFieldsService,
  CustomFieldType,
  ResourceCustomField,
} from '@freelancer/custom-fields';
import { User } from '@freelancer/datastore/collections';
import { HeadingType } from '@freelancer/ui/heading';
import { LinkWeight } from '@freelancer/ui/link';
import { Margin } from '@freelancer/ui/margin';
import { TextSize } from '@freelancer/ui/text';
import { ResourceTypeApi } from 'api-typings/resources/metadata';
import * as Rx from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-user-profile-practitioner-information',
  template: `
    <ng-template #PractitionerInformationBody>
      <ng-container
        *ngIf="!(showDataNotFoundMessage$ | flAsync); else dataNotFoundMessage"
      >
        <fl-grid
          *ngIf="(resourceCustomField$ | flAsync)?.customField as customField"
        >
          <fl-col [col]="12" [colTablet]="6">
            <fl-bit
              class="PractitionerInformationItem"
              [flMarginBottom]="Margin.XXSMALL"
            >
              <fl-link
                link="{{ userDeloitteProfileLink$ | flAsync }}"
                i18n="DPN profile link label"
                flTrackingLabel="goToDPNProfile"
                [weight]="LinkWeight.BOLD"
              >
                {{ customField.firstname?.value }}
                {{ customField.lastname?.value }} DPN Profile
              </fl-link>
            </fl-bit>

            <app-user-profile-practitioner-information-item
              itemLabel="Level"
              i18n-itemLabel="Level label"
              [itemValue]="customField.level?.value"
            >
            </app-user-profile-practitioner-information-item>

            <app-user-profile-practitioner-information-item
              itemLabel="Legal entity"
              i18n-itemLabel="Legal entity label"
              [itemValue]="customField.legal_entity_alignment?.value"
            >
            </app-user-profile-practitioner-information-item>

            <app-user-profile-practitioner-information-item
              itemLabel="Practice"
              i18n-itemLabel="Practice label"
              [itemValue]="customField.practice?.value"
            >
            </app-user-profile-practitioner-information-item>

            <app-user-profile-practitioner-information-item
              itemLabel="Offering portfolio"
              i18n-itemLabel="Offering portfolio label"
              [itemValue]="customField.offering_portfolio?.value"
            >
            </app-user-profile-practitioner-information-item>
          </fl-col>

          <fl-col [col]="12" [colTablet]="6">
            <app-user-profile-practitioner-information-item
              itemLabel="Independence ruleset"
              i18n-itemLabel="Independence ruleset label"
              [itemValue]="customField.independence_ruleset?.value"
            >
            </app-user-profile-practitioner-information-item>

            <app-user-profile-practitioner-information-item
              itemLabel="Phone number cell"
              i18n-itemLabel="Phone number cell label"
              [itemValue]="customField.phone_number_work_cell?.value"
            >
            </app-user-profile-practitioner-information-item>

            <app-user-profile-practitioner-information-item
              itemLabel="Phone number skype"
              i18n-itemLabel="Phone number skype label"
              [itemValue]="customField.phone_number_skype?.value"
            >
            </app-user-profile-practitioner-information-item>

            <app-user-profile-practitioner-information-item
              itemLabel="Member firm alignment"
              i18n-itemLabel="Member firm alignment label"
              [itemValue]="customField.member_firm_alignment?.value"
            >
            </app-user-profile-practitioner-information-item>

            <app-user-profile-practitioner-information-item
              itemLabel="Country alignment"
              i18n-itemLabel="Country alignment"
              [itemValue]="customField.country_alignment?.value"
            >
            </app-user-profile-practitioner-information-item>
          </fl-col>

          <fl-col [col]="12">
            <app-user-profile-practitioner-information-item
              itemLabel="Email address"
              i18n-itemLabel="Email address label"
              [itemValue]="customField.email_address?.value"
            >
            </app-user-profile-practitioner-information-item>
          </fl-col>
          <fl-col [col]="12">
            <app-user-profile-practitioner-information-item
              itemLabel="Certifications"
              i18n-itemLabel="Certifications label"
              [itemValue]="userCertifications$ | flAsync"
            >
            </app-user-profile-practitioner-information-item>
          </fl-col>
        </fl-grid>
      </ng-container>
      <ng-template #dataNotFoundMessage>
        <fl-text i18n="Data not found message">
          No data found on MDR. Please contact the MyGigs support team.
        </fl-text>
      </ng-template>
    </ng-template>

    <ng-container *ngIf="isProfilePage; else notProfilePage">
      <fl-card [flMarginBottom]="Margin.SMALL">
        <fl-card-header-title i18n="Practitioner information Card Title">
          Practitioner information
        </fl-card-header-title>
        <ng-template
          [ngTemplateOutlet]="PractitionerInformationBody"
        ></ng-template>
      </fl-card>
    </ng-container>

    <ng-template #notProfilePage>
      <fl-heading
        i18n="Practitioner information"
        [flMarginBottom]="Margin.SMALL"
        [headingType]="HeadingType.H3"
        [size]="TextSize.SMALL"
      >
        Practitioner information
      </fl-heading>
      <ng-template
        [ngTemplateOutlet]="PractitionerInformationBody"
      ></ng-template>
    </ng-template>
  `,
})
export class UserProfilePractitionerInformationComponent implements OnInit {
  LinkWeight = LinkWeight;
  Margin = Margin;
  CustomFieldType = CustomFieldType;
  HeadingType = HeadingType;
  TextSize = TextSize;

  @Input() user$: Rx.Observable<User>;
  @Input() isViewerProfileOwner$: Rx.Observable<boolean>;
  @Input() isProfilePage = false;

  showDataNotFoundMessage$: Rx.Observable<boolean>;
  resourceCustomField$: Rx.Observable<ResourceCustomField>;
  userCertifications$: Rx.Observable<string | undefined>;
  userDeloitteProfileLink$: Rx.Observable<string | undefined>;

  constructor(private customFieldsService: CustomFieldsService) {}

  ngOnInit(): void {
    this.resourceCustomField$ = this.user$.pipe(
      switchMap(
        user =>
          this.customFieldsService.getCustomFields(
            Rx.of(user.customFieldValues),
            ResourceTypeApi.USER,
            Rx.of(user.id),
          ).valueChanges$,
      ),
    );

    this.userCertifications$ = this.resourceCustomField$.pipe(
      map(resourceCustomField => {
        if (
          resourceCustomField.customField.certifications?.type ===
            CustomFieldType.STRING_ARRAY &&
          resourceCustomField.customField.certifications.value[0]
        ) {
          return resourceCustomField.customField.certifications.value.join(
            ', ',
          );
        }
        return undefined;
      }),
    );

    this.userDeloitteProfileLink$ = this.resourceCustomField$.pipe(
      map(resourceCustomField => {
        if (
          resourceCustomField.customField.email_address?.type ===
            CustomFieldType.STRING &&
          resourceCustomField.customField.email_address.value
        ) {
          return `https://people.deloitteresources.com/dpn/index.html#/Profile/${
            resourceCustomField.customField.email_address.value.split('@')[0]
          }`;
        }
        return undefined;
      }),
    );

    // For the profile owner without custom field values, show data not found message
    this.showDataNotFoundMessage$ = Rx.combineLatest([
      this.isViewerProfileOwner$,
      this.user$.pipe(map(user => user.customFieldValues)),
    ]).pipe(
      map(
        ([isViewerProfileOwner, userCustomFieldValues]) =>
          isViewerProfileOwner && userCustomFieldValues.length === 0,
      ),
    );
  }
}
